<template>
  <main>
    <GridsOutterGrid>
      <GridsTwelveGrid class="col-span-1 col-start-2">
        <SectionSet is-first class="col-span-6 col-start-1 laptop:col-span-10 laptop:col-start-2">
          <GridsTwelveGrid>
            <BreadCrumbs class="col-span-3 col-start-1 laptop:col-start-1" />
            <h1 class="col-span-6 col-start-1 row-start-2 laptop:col-span-7 laptop:col-start-1">
              {{ brandsPageData?.brandsPage?.data?.attributes?.hero_title }}
            </h1>
            <BrandsBrandPicker
              :brands="brandsPageData"
              :selectedBrand="selectedBrand"
              @selected="(selectedBrand) => setSelectedBrand(selectedBrand)"
              class="col-span-6 col-start-1 mt-12 hidden tablet:col-span-12 tablet:col-start-1 laptop:flex desktop:mt-10"
            />
            <BrandsBrandPickerMobile
              :brands="brandsPageData"
              :selectedBrand="selectedBrand"
              @selected="(selectedBrand) => setSelectedBrand(selectedBrand)"
              class="col-span-6 col-start-1 mt-12 tablet:col-span-12 tablet:col-start-1 laptop:hidden desktop:col-span-8 desktop:col-start-3 desktop:mt-10"
            />
          </GridsTwelveGrid>
          <div
            class="col-span-6 col-start-1 mt-12 laptop:col-span-4 laptop:col-start-5 desktop:mt-10"
            ref="productsBrandHeading"
          >
            <div class="flex items-center justify-center text-center transition-all duration-500 ease-out">
              <Transition name="fade-slide-up" mode="out-in">
                <div :key="selectedBrand?.attributes?.slug">
                  <span
                    class="text-lg font-normal uppercase leading-[1.2] tracking-[0.08em] text-ng70 transition-all duration-200 ease-out"
                  >
                    {{ $t("products") }}
                  </span>
                  <span class="mx-2 leading-[1.2] tracking-[0.08em] text-ng70">|</span>
                  <span v-if="selectedBrand" class="uppercase leading-[1.2] tracking-[0.08em] text-ng800">{{
                    selectedBrand?.attributes?.name
                  }}</span>
                  <span v-else class="uppercase leading-[1.2] tracking-[0.08em] text-ng800">{{ $t("all") }}</span>
                </div>
              </Transition>
            </div>
          </div>
          <div class="mt-8 laptop:mt-10">
            <FiltersFilterBar :filters="filters" :filteredProducts="filteredProducts" class="hidden laptop:flex" />
            <FiltersMobileFiltersDropdown class="block laptop:hidden" />
            <FiltersFilterSideDrawer :filters="filters" />
          </div>
          <div class="pt-8 transition-all duration-300 ease-out laptop:pt-10" ref="brandProductGridContainer">
            <div
              class="mb-8 flex min-h-[21px] justify-center leading-[20.8px] text-ng300 laptop:mb-10"
              ref="filteredProductsCountDiv"
            >
              <Transition name="fade-slide-up" mode="out-in">
                <span :key="productsData?.products?.meta?.pagination?.total" class="delay-500" v-if="!queryPending">
                  {{ $t("products-found", { count: productsData?.products?.meta?.pagination?.total }) }}
                </span>
              </Transition>
            </div>

            <Transition name="fade-slide-up-alt" mode="out-in" @after-enter="afterBrandProductsAppear" appear>
              <GridsProductGrid
                :class="{
                  'min-h-[386px] transition duration-200 ease-out': queryPending,
                }"
                ref="productGrid"
                :key="JSON.stringify(productsData.products?.data)"
                :products="productsData.products?.data"
                :brand="selectedBrand?.attributes?.slug"
              />
            </Transition>
            <Transition name="fade-slide-up" mode="out-in" appear>
              <Pagination
                :totalPages="productsData?.products?.meta?.pagination?.pageCount"
                :currentPage="productsData?.products?.meta?.pagination?.page - 1"
                @update:current-page="(page) => setCurrentPage(page)"
                class="mt-8 laptop:mt-10"
              />
            </Transition>
          </div>
        </SectionSet>
        <h4 class="col-span-6 col-start-1 mt-25 text-center laptop:col-span-6 laptop:col-start-4 laptop:mt-36">
          {{ brandsPageData?.brandsPage?.data?.attributes?.tiles_title }}
        </h4>
        <SectionSet class="col-span-6 col-start-1 mt-8 laptop:col-span-10 laptop:col-start-2 laptop:!mt-8" isLast>
          <div class="flex w-full flex-col space-y-6 laptop:flex-row laptop:space-x-6 laptop:space-y-0">
            <LinkCard
              bg="bg-[#F6EEEB]"
              :title="brandsPageData?.brandsPage?.data?.attributes?.tiles_tile_private_label?.title || ''"
              :subtitle="brandsPageData?.brandsPage?.data?.attributes?.tiles_tile_private_label?.subtitle || ''"
              :img="
                brandsPageData?.brandsPage?.data?.attributes?.tiles_tile_private_label?.image?.data?.attributes?.url ||
                ''
              "
              :link="localePath({ name: 'for-business-private-label' })"
              arrow="#B06C4C"
            />
            <LinkCard
              bg="bg-[#EBF5F1]"
              :title="brandsPageData?.brandsPage?.data?.attributes?.tiles_tile_packaging?.title || ''"
              :subtitle="brandsPageData?.brandsPage?.data?.attributes?.tiles_tile_packaging?.subtitle || ''"
              :img="
                brandsPageData?.brandsPage?.data?.attributes?.tiles_tile_packaging?.image?.data?.attributes?.url || ''
              "
              :link="localePath({ name: 'products-packaging' })"
              arrow="#647E8D"
            />
          </div>
          <div class="mt-6 flex w-full items-center justify-center">
            <NextBtn :text="$t('Contact us')" :to="localePath({ name: 'contact' })" />
          </div>
        </SectionSet>
      </GridsTwelveGrid>
    </GridsOutterGrid>
  </main>
</template>
<script setup lang="ts">
import { GridsProductGrid } from "#components";
import { BrandsKey, SelectedBrandKey } from "~/symbols/symbols";
import { BrandWithProductsFragment, ProductFragment } from "#gql";
import { useFilters } from "~/stores/filters";
import { LoadingBarKey } from "~/symbols/symbols";
import { removePxFromStyle } from "~/utilities/utils";
import { storeToRefs } from "pinia";

let lenis = null;

if (process.client) {
  lenis = inject("lenis");
}

const PRODUCTS_PER_PAGE = 16;

const loadingBarIndicator = inject(LoadingBarKey);

const { t: $t } = useI18n();
const filterStore = useFilters();
const filteredProductsCount = ref(0);
const currentPage = ref(0);
const filteredProductsCountDiv = ref<HTMLElement | null>(null);

const totalPages = computed(() => Math.ceil(filteredProductsCount.value / PRODUCTS_PER_PAGE));

const route = useRoute();
const router = useRouter();
const pageTitle = ref("");
const localePath = useLocalePath();

const filters = ref<FilterCategory[]>([
  {
    id: 1,
    slug: "product_type",
    filterCategory: "product_types",
    name: $t("FiltersNames.ProductType"),
    filters: [] as Filter[],
  },
  {
    id: 2,
    slug: "brands",
    filterCategory: "brands",
    name: $t("FiltersNames.Brand"),
    filters: [] as Filter[],
  },
  {
    id: 3,
    slug: "product_type_option",
    filterCategory: "product_type_options",
    name: $t("FiltersNames.Variety"),
    filters: [] as Filter[],
  },
  {
    id: 4,
    slug: "product_cuts",
    filterCategory: "product_cuts",
    name: $t("FiltersNames.Cut"),
    filters: [] as Filter[],
  },
  {
    id: 5,
    slug: "product_ingredients",
    filterCategory: "product_ingredients",
    name: $t("FiltersNames.Ingredients"),
    filters: [] as Filter[],
  },
  // {
  //   id: 6,
  //   slug: "product_sizes",
  //   filterCategory: "product_sizes",
  //   name: $t("FiltersNames.Size"),
  //   filters: [] as Filter[],
  // },
]);

const selectedBrand = ref<BrandWithProductsFragment | null | undefined>(null);
provide(SelectedBrandKey, selectedBrand);

const products = ref<ProductFragment[] | null | undefined>(null);
const queryPending = ref<boolean>(false);
const { locale, finalizePendingLocaleChange } = useI18n();

await finalizePendingLocaleChange();

const currentPageNumber = computed(() => {
  return route.query?.page ? parseInt(route.query?.page) : 1;
});

const currentBrand = computed(() => {
  return route.query?.brand;
});

//@ts-ignore
const parseFilter = (filterData) => {
  if (!(filterData.data instanceof Array)) {
    return [];
  }

  //@ts-ignore
  return filterData.data.map((f) => {
    const returnData = {
      id: f.id,
      slug: f.attributes?.slug,
      name: f.attributes?.name,
      icon: f.attributes?.icon?.data?.attributes,
    };

    return returnData;
  });
};

loadingBarIndicator?.value?.indicator?.start();

const { data: brandsPageData } = await useAsyncGql({
  operation: "BrandsPage",
  variables: { locale: locale.value },
  options: {
    lazy: false,
  },
});

filters.value.forEach((filter) => {
  switch (filter.filterCategory) {
    case "product_types":
      filter.filters = parseFilter(brandsPageData.value.productTypes);
      break;
    case "product_type_options":
      filter.filters = parseFilter(brandsPageData.value.productTypeOptions);
      break;
    case "product_cuts":
      filter.filters = parseFilter(brandsPageData.value.productCuts);
      break;
    case "product_ingredients":
      filter.filters = parseFilter(brandsPageData.value.productIngredients);
      break;
    case "product_sizes":
      filter.filters = parseFilter(brandsPageData.value.productSizes);
      break;
  }
});

const { activeFilters } = storeToRefs(filterStore);

const ptFilters = computed(() => {
  const filter = activeFilters.value.find((f) => {
    return f.slug === "product_type";
  });

  return filter?.filters;
});

const pcFilters = computed(() => {
  const filter = activeFilters.value.find((f) => {
    return f.slug === "product_cuts";
  });

  return filter?.filters;
});

const psFilters = computed(() => {
  const filter = activeFilters.value.find((f) => {
    return f.slug === "product_sizes";
  });

  return filter?.filters;
});

const piFilters = computed(() => {
  const filter = activeFilters.value.find((f) => {
    return f.slug === "product_ingredients";
  });

  return filter?.filters;
});

const ptoFilters = computed(() => {
  const filter = activeFilters.value.find((f) => {
    return f.slug === "product_type_option";
  });

  return filter?.filters;
});

watch([ptFilters, pcFilters, psFilters, piFilters, ptoFilters], (newVal) => {
  if (newVal) {
    router.push({
      query: {
        ...route.query,
        page: 1,
      },
    });
    currentPage.value = 0;
  }
});

//@ts-ignore
const { data: productsData } = await useAsyncGql({
  operation: "BrandsPageProducts",
  variables: {
    locale: locale.value,
    brand_slug: currentBrand,
    page: currentPageNumber,
    product_type: ptFilters,
    product_cuts: pcFilters,
    product_sizes: psFilters,
    product_ingredients: piFilters,
    product_type_options: ptoFilters,
  },
});

loadingBarIndicator?.value?.indicator?.finish();

provide(BrandsKey, brandsPageData);

const brand = brandsPageData.value?.productBrands?.data.find((brand) => brand.attributes?.slug === route.query.brand);

selectedBrand.value = brand;

products.value = brandsPageData.value?.products?.data;

generateFilters(brandsPageData.value?.products?.data);

watch(
  brandsPageData,
  (newVal) => {
    if (newVal) {
      if (newVal.brandsPage?.data?.attributes?.seo?.metaTitle) {
        pageTitle.value = newVal.brandsPage?.data?.attributes?.seo?.metaTitle;
      }
      useHead({
        title: () => pageTitle.value,
      });
      useServerSeoMeta({
        title: () => `${newVal.brandsPage?.data?.attributes?.seo?.metaTitle}`,
        ogTitle: () => `${newVal.brandsPage?.data?.attributes?.seo?.metaTitle}`,
        description: () => `${newVal.brandsPage?.data?.attributes?.seo?.metaDescription}`,
        ogDescription: () => `${newVal.brandsPage?.data?.attributes?.seo?.metaDescription}`,
        ogImage: () => `${newVal.brandsPage?.data?.attributes?.seo?.metaImage?.data?.attributes?.url}`,
      });
    }
  },
  { immediate: true },
);

watch(
  () => route.query.brand,
  (slug) => {
    products.value = null;
    if (slug) {
      const brand = brandsPageData.value?.productBrands?.data.find((brand) => brand.attributes?.slug === slug);
      if (brand) {
        selectedBrand.value = brand;
        getBrandProducts(brand.attributes?.slug as string);
      } else {
        router.push({ query: { brand: undefined } });
      }
    } else {
      selectedBrand.value = null;

      getBrandProducts(null);
    }
  },
);

const brandsArray: Filter[] | undefined = brandsPageData.value?.productBrands?.data.map((brand) => {
  return {
    id: brand.id,
    slug: brand.attributes?.slug ?? "",
    name: brand.attributes?.name,
    icon: brand.attributes?.logo?.data?.attributes,
  };
});

const brandsFilter = filters.value.find((filter) => filter.slug === "brands");

if (brandsFilter) {
  brandsFilter.filters = brandsArray ?? [];
}

const filteredProducts = computed(() => {
  if (!products.value) return [];
  let filteredArray = [...products.value];

  filterStore.activeFilters.forEach((filterCategory) => {
    if (filterCategory.filters.length === 0) return;
    filteredArray = filteredArray.filter((product) => {
      if (product && product.attributes && filterCategory.slug in product?.attributes) {
        const key = filterCategory.slug as keyof typeof product.attributes;
        const productAttribute = product.attributes[key] as ProductFilterData;

        if (Array.isArray(productAttribute.data)) {
          return productAttribute.data.some((attribute) => {
            return filterCategory.filters.includes(attribute.attributes.slug);
          });
        } else {
          return filterCategory.filters.includes(productAttribute.data?.attributes?.slug);
        }
      } else {
        return product;
      }
    });
  });

  filteredProductsCount.value = filteredArray.length;

  return filteredArray;
});

const paginatedProducts = computed(() => {
  const start = currentPage.value * PRODUCTS_PER_PAGE;
  const end = start + PRODUCTS_PER_PAGE;

  return filteredProducts.value.slice(start, end);
});

watch(
  () => filteredProducts.value,
  (newVal) => {
    if (newVal.length) {
      router.push({
        query: {
          ...route.query,
        },
      });
      currentPage.value = 0;
    }
  },
  { deep: true, immediate: true },
);

const productsBrandHeading = ref(null);

function setCurrentPage(page: number) {
  router.push({
    query: {
      ...route.query,
      page: page + 1,
    },
  });

  if (lenis) {
    lenis.scrollTo(productsBrandHeading.value, { offset: -10 });
  }
}

/*watch(
  () => route.query.page,
  (page) => {
    if (page) {
      if (filteredProducts.value.length < Math.ceil((parseInt(page as string) - 1) * PRODUCTS_PER_PAGE)) {
        router.push({
          query: {
            ...route.query,
            page: 1,
          },
        });
        return;
      }
      currentPage.value = parseInt(page as string) - 1;
    }
  },
  { immediate: true },
);*/

async function getBrandProducts(brand: string | null) {
  filterStore.clearAllFilters();

  /*if (brand) {
    queryPending.value = true;
    loadingBarIndicator?.value?.indicator?.start();
    const { data: brandProducts } = await useAsyncGql({
      operation: "GetProductsByBrandSlug",
      variables: { locale: locale.value, brand_slug: brand, start: 0, limit: -1 },
      options: {
        lazy: false,
      },
    });
    loadingBarIndicator?.value?.indicator?.finish();
    queryPending.value = false;
    products.value = brandProducts.value?.products?.data;

    generateFilters(brandProducts.value?.products?.data);
  } else {
    queryPending.value = true;
    loadingBarIndicator?.value?.indicator?.start();

    const { data: allProducts } = await useAsyncGql({
      operation: "GetAllProducts",
      variables: { locale: locale.value, start: 0, limit: -1 },
      options: {
        lazy: false,
      },
    });
    loadingBarIndicator?.value?.indicator?.finish();

    queryPending.value = false;

    products.value = allProducts.value?.products?.data;

    generateFilters(allProducts.value?.products?.data);
  }*/
}

function generateFilters(products: ProductFragment[] | undefined) {
  if (products) {
    let productTypes: Filter[] = [];
    let productVarieties: Filter[] = [];
    let productCuts: Filter[] = [];
    let productIngredients: Filter[] = [];
    let productSizes: Filter[] = [];

    products.forEach((product) => {
      if (!productTypes.some((type) => type.slug === product.attributes?.product_type?.data?.attributes?.slug)) {
        if (product.attributes?.product_type?.data?.attributes?.slug === undefined) return;
        productTypes.push({
          id: product.attributes?.product_type?.data?.id,
          slug: product.attributes?.product_type?.data?.attributes?.slug as string,
          name: product.attributes?.product_type?.data?.attributes?.name,
          icon: product.attributes?.product_type?.data?.attributes?.icon?.data?.attributes,
        });
      }

      if (
        !productVarieties.some((type) => type.slug === product.attributes?.product_type_option?.data?.attributes?.slug)
      ) {
        if (product.attributes?.product_type_option?.data?.attributes?.slug === undefined) return;
        productVarieties.push({
          id: product.attributes?.product_type_option?.data?.id,
          slug: product.attributes?.product_type_option?.data?.attributes?.slug as string,
          name: product.attributes?.product_type_option?.data?.attributes?.name,
          icon: product.attributes?.product_type_option?.data?.attributes?.icon?.data?.attributes,
        });
      }

      product.attributes?.product_cuts?.data.forEach((productCut) => {
        if (!productCuts.some((type) => type.slug === productCut.attributes?.slug)) {
          if (productCut.attributes?.slug === undefined) return;

          productCuts.push({
            id: productCut.id,
            slug: productCut.attributes?.slug,
            name: productCut.attributes?.name,
            icon: productCut.attributes?.icon?.data?.attributes,
          });
        }
      });

      product.attributes?.product_ingredients?.data.forEach((productIngredient) => {
        if (!productIngredients.some((type) => type.slug === productIngredient.attributes?.slug)) {
          if (productIngredient.attributes?.slug === undefined) return;

          productIngredients.push({
            id: productIngredient.id,
            slug: productIngredient.attributes?.slug,
            name: productIngredient.attributes?.name,
            icon: productIngredient.attributes?.icon?.data?.attributes,
          });
        }
      });

      product.attributes?.product_sizes?.data.forEach((productSize) => {
        if (!productSizes.some((type) => type.slug === productSize.attributes?.slug)) {
          if (productSize.attributes?.slug === undefined) return;

          productSizes.push({
            id: productSize.id,
            slug: productSize.attributes?.slug,
            name: productSize.attributes?.name,
          });
        }
      });
    });

    filters.value.forEach((filter) => {
      switch (filter.filterCategory) {
        case "product_types":
          filter.filters = productTypes;
          break;
        case "product_type_options":
          filter.filters = productVarieties;
          break;
        case "product_cuts":
          filter.filters = productCuts;
          break;
        case "product_ingredients":
          filter.filters = productIngredients;
          break;
        case "product_sizes":
          filter.filters = productSizes;
          break;
      }
    });
  }
}

function setSelectedBrand(brand: BrandWithProductsFragment | null | undefined) {
  if (brand !== null && brand !== undefined) {
    router.push({ query: { brand: brand.attributes?.slug } });
  } else {
    router.push({ query: { brand: undefined } });
  }

  if (lenis) {
    lenis.scrollTo(productsBrandHeading.value, { offset: -10 });
  }
}

const productGrid = ref<InstanceType<typeof GridsProductGrid> | null>(null);
const brandProductGridContainer = ref<HTMLElement | null>(null);

function afterBrandProductsAppear() {
  if (brandProductGridContainer.value && productGrid.value?.productGrid?.offsetHeight !== undefined) {
    const filteredProductsCountDivTotalHeight =
      (filteredProductsCountDiv.value?.offsetHeight || 0) +
      removePxFromStyle(getComputedStyle(filteredProductsCountDiv.value as Element).marginBottom);

    const brandProductGridPadding = removePxFromStyle(
      getComputedStyle(brandProductGridContainer.value as Element).paddingTop,
    );

    brandProductGridContainer.value.style.minHeight =
      productGrid.value?.productGrid?.offsetHeight +
      brandProductGridPadding +
      filteredProductsCountDivTotalHeight +
      "px";
  }
}

onBeforeUnmount(() => {
  selectedBrand.value = null;
  filterStore.clearAllFilters();
});
</script>
<style lang="scss" scoped></style>
